import React, { useMemo } from 'react'
import { useAtomValue } from 'jotai'
import { FieldErrors, UseFormRegister, UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Avatar, Box, Button } from '@node-space/ui-components'
import { useGetCustomersWalletsQuery } from 'hooks/queries/Customers/useGetCustomersWallets'
import { customerActionAtom } from 'pages/Customers/atoms/customersAtoms'
import { Customer, SetCustomerFeeFormData } from 'types/customers'
import { CustomerFeeSetupStep, CustomerFeeSetupSteps, feeStructureOptions } from '../../types'
import { CustomerSpecificFeeCard } from '../CustomerSpecificFeeCard'

interface SetCustomerFeeProps {
  customer: Customer
  setStep: (step: CustomerFeeSetupStep) => void
  formValues: SetCustomerFeeFormData
  errors: FieldErrors<SetCustomerFeeFormData>
  register: UseFormRegister<SetCustomerFeeFormData>
  setValue: UseFormSetValue<SetCustomerFeeFormData>
}

export const SetCustomerFee = ({
  customer,
  setStep,
  formValues,
  errors,
  register,
  setValue,
}: SetCustomerFeeProps) => {
  const { t } = useTranslation()
  const customerAction = useAtomValue(customerActionAtom)

  const { data: customerWalletsData, isLoading: isLoadingWallets } = useGetCustomersWalletsQuery({
    customerReference: customer?.reference,
  })

  const currencyOptions = useMemo(() => {
    if (!customerWalletsData?.content) return []

    const currencyList = customerWalletsData?.content?.reduce((acc, wallet) => {
      if (wallet?.balance?.currencyCode) {
        acc?.push(wallet?.balance?.currencyCode)
      }
      return acc
    }, [])

    return currencyList
      ?.sort((a, b) => a.localeCompare(b))
      .map(currency => ({
        value: currency,
        leftText: currency,
        LeftElement: <Avatar name={currency} size="small" />,
      }))
  }, [customerWalletsData?.content])

  const handleSetCustomerFee = async () => {
    setStep(CustomerFeeSetupSteps.SUCCESS)
  }

  const handleClose = () => {
    customerAction?.onClose?.()
  }

  return (
    <Box flexCol gapY={24} width="full" padding={24}>
      <Box
        padding={24}
        paddingB={28}
        className="border border-solid border-stroke-interactive rounded-lg"
      >
        <CustomerSpecificFeeCard
          currencyOptions={currencyOptions}
          feeStructureOptions={feeStructureOptions}
          formValues={formValues}
          errors={errors}
          register={register}
          setValue={setValue}
          isLoadingWallets={isLoadingWallets}
        />
      </Box>

      <Box flex justify="between" width="full">
        <Button variant="secondary" onClick={handleClose}>
          {t('cancel')}
        </Button>
        <Button onClick={handleSetCustomerFee}>{t('confirm')}</Button>
      </Box>
    </Box>
  )
}
